import React, { useState, useEffect } from 'react'
import {
  TextField,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Stack,
  Button,
  Alert,
  Popover,
  useTheme,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import SettingsIcon from '@mui/icons-material/Settings'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import LinkIcon from '@mui/icons-material/Link'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import FactoryIcon from '@mui/icons-material/Factory'
import StorefrontIcon from '@mui/icons-material/Storefront'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AdvancedDemandDialog from './AdvancedDemandDialog'
import SaveIndicator from './SaveIndicator'
import { useCurrency, CURRENCIES } from '../contexts/CurrencyContext'

// ── Node type config ────────────────────────────────────────────────
const NODE_TYPE = {
  supplier:    { color: '#10b981', Icon: WarehouseIcon,     label: 'Supplier'     },
  factory:     { color: '#f59e0b', Icon: FactoryIcon,       label: 'Factory'      },
  distributor: { color: '#3b82f6', Icon: LocalShippingIcon, label: 'Distributor'  },
  retailer:    { color: '#8b5cf6', Icon: StorefrontIcon,    label: 'Retailer'     },
}

// ── Currency Selector ───────────────────────────────────────────────
function CurrencySelector({ isDark, border, textPrimary, textSecondary, surface }) {
  const { currency, setCurrency } = useCurrency()
  const [anchor, setAnchor] = useState(null)

  return (
    <>
      <Box
        onClick={e => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.625,
          px: 1, py: 0.5, borderRadius: '8px',
          border: `1px solid ${border}`,
          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          cursor: 'pointer', userSelect: 'none',
          transition: 'all 0.15s',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
        }}
      >
        <Typography sx={{ fontSize: '13px', lineHeight: 1, userSelect: 'none' }}>
          {currency.flag}
        </Typography>
        <Typography sx={{
          fontSize: '12px', fontWeight: 800, color: textPrimary,
          fontFamily: '"DM Mono", monospace', letterSpacing: '0.01em',
        }}>
          {currency.symbol}
        </Typography>
        <Typography sx={{ fontSize: '9.5px', fontWeight: 600, color: textSecondary, letterSpacing: '0.04em' }}>
          {currency.code}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 12, color: textSecondary }} />
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.75, borderRadius: '12px', overflow: 'hidden', minWidth: 220,
            border: `1px solid ${border}`,
            bgcolor: isDark ? '#131f35' : '#ffffff',
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography sx={{
            fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em',
            color: textSecondary, px: 1, py: 0.75,
          }}>
            Select Currency
          </Typography>
          {CURRENCIES.map(c => (
            <Box
              key={c.code}
              onClick={() => { setCurrency(c); setAnchor(null) }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 1, py: 0.75, borderRadius: '8px', cursor: 'pointer',
                bgcolor: c.code === currency.code
                  ? (isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)')
                  : 'transparent',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                transition: 'background 0.12s',
              }}
            >
              <Typography sx={{ fontSize: '14px', lineHeight: 1, userSelect: 'none' }}>{c.flag}</Typography>
              <Typography sx={{
                fontSize: '12px', fontWeight: 800, color: textPrimary, minWidth: 28,
                fontFamily: '"DM Mono", monospace',
              }}>
                {c.symbol}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: textPrimary, lineHeight: 1.2 }}>
                  {c.code}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: textSecondary, lineHeight: 1.2 }}>
                  {c.name}
                </Typography>
              </Box>
              {c.code === currency.code && (
                <CheckIcon sx={{ fontSize: 13, color: '#6366f1' }} />
              )}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}

// ── Section label ───────────────────────────────────────────────────
function SectionLabel({ children, color, mt = 2 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt, mb: 1.25 }}>
      <Box sx={{ width: 2, height: 12, borderRadius: 1, bgcolor: color || '#6366f1', flexShrink: 0 }} />
      <Typography sx={{
        fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em',
        color: color || '#6366f1', opacity: 0.9,
      }}>
        {children}
      </Typography>
    </Box>
  )
}

// ── Config header (module-level to avoid remount on every render) ────
function ConfigHeader({ currentNetworkName, saveStatus, onRenameNetwork }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // Blend with the panel background — no raised surface
  const panelBg       = isDark ? '#0d1626' : '#f0f4f8'
  const border        = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)'
  const textPrimary   = isDark ? '#cbd5e1' : '#334155'
  const textMuted     = isDark ? 'rgba(100,116,139,0.65)' : 'rgba(100,116,139,0.8)'
  const accent        = '#6366f1'

  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState(currentNetworkName)

  useEffect(() => {
    if (!editing) setDraftName(currentNetworkName)
  }, [currentNetworkName, editing])

  return (
    <Box sx={{
      px: 2, height: 40, flexShrink: 0,
      borderBottom: `1px solid ${border}`,
      bgcolor: panelBg,
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      {/* Network name — left */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TextField
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { onRenameNetwork(draftName); setEditing(false) }
                if (e.key === 'Escape') { setEditing(false) }
              }}
              onBlur={() => { onRenameNetwork(draftName); setEditing(false) }}
              size="small"
              autoFocus
              variant="standard"
              sx={{
                flex: 1,
                '& .MuiInput-root': { fontSize: '12px', fontWeight: 600, color: textPrimary, letterSpacing: '-0.01em' },
                '& .MuiInput-underline:after': { borderColor: accent },
                '& .MuiInput-underline:before': { borderColor: border },
              }}
            />
            <IconButton size="small" onClick={() => { onRenameNetwork(draftName); setEditing(false) }}
              sx={{ width: 20, height: 20, color: '#10b981', flexShrink: 0 }}>
              <CheckIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Stack>
        ) : (
          <Box
            onClick={() => { setDraftName(currentNetworkName); setEditing(true) }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'text', minWidth: 0,
              '&:hover .pencil': { opacity: 0.5 } }}
          >
            <Typography sx={{
              fontSize: '12px', fontWeight: 600, color: textPrimary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}>
              {currentNetworkName}
            </Typography>
            <EditIcon className="pencil" sx={{ fontSize: 9, color: textMuted, opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }} />
          </Box>
        )}
      </Box>

      {/* Right: save indicator · currency */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        <SaveIndicator status={saveStatus} />
        <CurrencySelector isDark={isDark} border={border} textPrimary={textPrimary} textSecondary={textMuted} surface={isDark ? '#131f35' : '#ffffff'} />
      </Stack>
    </Box>
  )
}

// ── Config card (module-level to avoid remount) ──────────────────────
function ConfigCard({ children, accentColor, sx: sxProp }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const surface = isDark ? '#131f35' : '#ffffff'
  const border  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  return (
    <Box sx={{
      mb: 2, borderRadius: '12px', border: `1px solid ${border}`,
      bgcolor: surface, overflow: 'hidden', ...sxProp,
    }}>
      {accentColor && <Box sx={{ height: 2, bgcolor: accentColor }} />}
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  )
}

// ── Node type badge (module-level to avoid remount) ──────────────────
function NodeTypeBadge({ type }) {
  const conf = NODE_TYPE[type] || { color: '#6366f1', Icon: AccountTreeIcon, label: type }
  const { color, Icon, label } = conf
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      px: 1, py: 0.375, borderRadius: '6px',
      bgcolor: `${color}18`,
    }}>
      <Icon sx={{ fontSize: 11, color }} />
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color, textTransform: 'capitalize', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
    </Box>
  )
}

// ── NumericField — controlled input with local draft so typing works properly ──
// Stores a local string while focused; only commits to store when value is valid.
// Syncs from the prop only when the field is not focused (e.g. node switch).
function NumericField({ value, onChange, fallback = 0, float = false, ...rest }) {
  const [draft, setDraft] = React.useState(value != null ? String(value) : '')
  const focused = React.useRef(false)

  React.useEffect(() => {
    if (!focused.current) setDraft(value != null ? String(value) : '')
  }, [value])

  return (
    <TextField
      {...rest}
      type="number"
      value={draft}
      onChange={e => {
        const raw = e.target.value
        setDraft(raw)
        const parsed = float ? parseFloat(raw) : parseInt(raw, 10)
        if (!isNaN(parsed) && raw.trim() !== '') onChange(parsed)
      }}
      onFocus={() => { focused.current = true }}
      onBlur={() => {
        focused.current = false
        const parsed = float ? parseFloat(draft) : parseInt(draft, 10)
        if (isNaN(parsed) || draft.trim() === '') {
          setDraft(String(fallback))
          onChange(fallback)
        }
      }}
    />
  )
}

// ── Policy params (shared by distributor, factory, retailer) ────────
function PolicyParams({ nodeData, nodeId, updateNodeData, defaults }) {
  const policy = nodeData.replenishment_policy || 'SS'
  const isSS = policy === 'SS' || policy === 'SS_safety' || !policy
  const isRQ = policy === 'RQ' || policy === 'RQ_safety'
  const hasSafety = policy === 'SS_safety' || policy === 'RQ_safety'

  return (
    <>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Policy Type</InputLabel>
        <Select
          value={nodeData.replenishment_policy ?? 'SS'}
          label="Policy Type"
          onChange={e => updateNodeData(nodeId, { replenishment_policy: e.target.value })}
        >
          <MenuItem value="SS">(s, S) — Continuous Review</MenuItem>
          <MenuItem value="SS_safety">(s, S) with Safety Stock</MenuItem>
          <MenuItem value="RQ">(R, Q) — Periodic Review</MenuItem>
          <MenuItem value="RQ_safety">(R, Q) with Safety Stock</MenuItem>
        </Select>
      </FormControl>

      {isSS && (
        <Stack spacing={1.25} sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={1.5}>
            <NumericField label="s — Reorder Point"
              value={nodeData.policy_s ?? defaults.s} fallback={defaults.s} fullWidth size="small"
              helperText="When to order" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
              onChange={v => updateNodeData(nodeId, { policy_s: v })}
            />
            <NumericField label="S — Order-up-to"
              value={nodeData.policy_S ?? defaults.S} fallback={defaults.S} fullWidth size="small"
              helperText="Target level" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
              onChange={v => updateNodeData(nodeId, { policy_S: v })}
            />
          </Stack>
          {hasSafety && (
            <NumericField label="Safety Stock"
              value={nodeData.safety_stock ?? (defaults.ss || 100)} fallback={defaults.ss || 100} fullWidth size="small"
              helperText="Buffer inventory minimum" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
              onChange={v => updateNodeData(nodeId, { safety_stock: v })}
            />
          )}
        </Stack>
      )}

      {isRQ && (
        <Stack spacing={1.25} sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={1.5}>
            <NumericField label="R — Review Period (days)"
              value={nodeData.policy_R ?? (defaults.R || 7)} fallback={defaults.R || 7} fullWidth size="small"
              helperText="Days between orders" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
              onChange={v => updateNodeData(nodeId, { policy_R: v })}
            />
            <NumericField label="Q — Order Quantity"
              value={nodeData.policy_Q ?? defaults.Q} fallback={defaults.Q} fullWidth size="small"
              helperText="Fixed order qty" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
              onChange={v => updateNodeData(nodeId, { policy_Q: v })}
            />
          </Stack>
          {hasSafety && (
            <NumericField label="Safety Stock"
              value={nodeData.safety_stock ?? (defaults.ss || 100)} fallback={defaults.ss || 100} fullWidth size="small"
              helperText="Buffer inventory minimum" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
              onChange={v => updateNodeData(nodeId, { safety_stock: v })}
            />
          )}
        </Stack>
      )}
    </>
  )
}

// ── Main component ──────────────────────────────────────────────────
function ConfigPanel({
  selectedNode,
  updateNodeData,
  selectedEdge,
  updateEdgeData,
  demands,
  updateDemand,
  deleteDemand,
  nodes,
  currentNetworkName,
  saveStatus,
  onRenameNetwork,
}) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { currency } = useCurrency()
  const sym = currency.symbol

  const [showAdvancedDemand, setShowAdvancedDemand] = useState(false)
  const [editingDemand, setEditingDemand]           = useState(null)

  // ── Design tokens ────────────────────────────────────────────────
  const bg          = isDark ? '#0d1626' : '#f0f4f8'
  const surface     = isDark ? '#131f35' : '#ffffff'
  const surfaceAlt  = isDark ? '#0a111e' : '#f8fafc'
  const border      = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  const borderMid   = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
  const textPrimary   = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#64748b' : '#64748b'
  const accent      = '#6366f1'

  // ── Shared field sx ──────────────────────────────────────────────
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '12.5px',
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)' },
      '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiInputLabel-root': { fontSize: '12px', color: textSecondary },
    '& .MuiFormHelperText-root': { fontSize: '10px', mt: 0.25 },
    '& .MuiSelect-select': { fontSize: '12.5px' },
  }

  const handleAdvancedEdit = demand => { setEditingDemand(demand); setShowAdvancedDemand(true) }
  const handleSaveAdvanced = demandData => { updateDemand(demandData.id, demandData); setEditingDemand(null); setShowAdvancedDemand(false) }
  const handleCloseAdvanced = () => { setEditingDemand(null); setShowAdvancedDemand(false) }

  // ── Empty state ───────────────────────────────────────────────────
  if (!selectedNode && !selectedEdge && demands.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: bg }}>
        <ConfigHeader currentNetworkName={currentNetworkName} saveStatus={saveStatus} onRenameNetwork={onRenameNetwork} />
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', p: 3, gap: 2,
        }}>
          {/* Geometric icon grid */}
          <Box sx={{ position: 'relative', width: 64, height: 64 }}>
            {[
              { top: 0, left: 0, color: '#10b981', Icon: WarehouseIcon },
              { top: 0, right: 0, color: '#f59e0b', Icon: FactoryIcon },
              { bottom: 0, left: 0, color: '#3b82f6', Icon: LocalShippingIcon },
              { bottom: 0, right: 0, color: '#8b5cf6', Icon: StorefrontIcon },
            ].map(({ Icon, color, ...pos }, i) => (
              <Box key={i} sx={{
                position: 'absolute', ...pos,
                width: 26, height: 26, borderRadius: '7px',
                bgcolor: `${color}18`, border: `1px solid ${color}28`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon sx={{ fontSize: 13, color, opacity: 0.7 }} />
              </Box>
            ))}
            <Box sx={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              width: 18, height: 18, borderRadius: '5px',
              bgcolor: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AccountTreeIcon sx={{ fontSize: 10, color: accent }} />
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: textPrimary, mb: 0.5 }}>
              Configuration Panel
            </Typography>
            <Typography sx={{ fontSize: '11.5px', color: textSecondary, lineHeight: 1.6 }}>
              Select a node or link to<br />configure its parameters
            </Typography>
          </Box>

          {/* Quick tip */}
          <Box sx={{
            mt: 1, px: 1.5, py: 1, borderRadius: '9px',
            bgcolor: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.05)',
            border: `1px solid ${isDark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.12)'}`,
          }}>
            <Typography sx={{ fontSize: '10.5px', color: textSecondary, textAlign: 'center', lineHeight: 1.6 }}>
              Currency set to{' '}
              <Box component="span" sx={{ fontWeight: 700, color: accent, fontFamily: '"DM Mono", monospace' }}>
                {currency.flag} {currency.symbol} {currency.code}
              </Box>
              <br />
              Change it anytime from the selector above
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: bg }}>
      <ConfigHeader currentNetworkName={currentNetworkName} saveStatus={saveStatus} onRenameNetwork={onRenameNetwork} />

      {/* Scrollable content */}
      <Box sx={{
        flex: 1, overflowY: 'auto', p: 1.75,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: border, borderRadius: 2 },
      }}>

        {/* ── Link Configuration ──────────────────────────────────── */}
        {selectedEdge && (
          <ConfigCard accentColor="#3b82f6">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.75 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '8px',
                bgcolor: 'rgba(59,130,246,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <LinkIcon sx={{ fontSize: 15, color: '#3b82f6' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: textPrimary, lineHeight: 1.2 }}>
                  Link Configuration
                </Typography>
                <Typography sx={{ fontSize: '10px', color: textSecondary }}>
                  {nodes.find(n => n.id === selectedEdge.source)?.data.label || 'Source'}
                  {' → '}
                  {nodes.find(n => n.id === selectedEdge.target)?.data.label || 'Target'}
                </Typography>
              </Box>
              <Chip label="Connection" size="small"
                sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(59,130,246,0.12)', color: '#3b82f6', '& .MuiChip-label': { px: 0.75 } }} />
            </Stack>

            <Stack spacing={1.5} sx={fieldSx}>
              {/* key forces fresh uncontrolled input when edge changes — fixes "only arrows work" bug */}
              <TextField
                key={`cost-${selectedEdge.id}`}
                label={`Transport Cost (${sym})`}
                type="number"
                defaultValue={selectedEdge.data?.cost ?? 10}
                onChange={e => {
                  const v = parseFloat(e.target.value)
                  if (!isNaN(v) && v >= 0) updateEdgeData(selectedEdge.id, { cost: v })
                }}
                fullWidth size="small" inputProps={{ step: '1', min: 0 }}
                helperText="Cost to ship goods through this link"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                key={`lt-${selectedEdge.id}`}
                label="Lead Time (days)"
                type="number"
                defaultValue={selectedEdge.data?.lead_time ?? 5}
                onChange={e => {
                  const v = parseFloat(e.target.value)
                  if (!isNaN(v) && v >= 0) updateEdgeData(selectedEdge.id, { lead_time: v })
                }}
                fullWidth size="small" inputProps={{ step: '1', min: 0 }}
                helperText="Transit time between nodes"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </ConfigCard>
        )}

        {/* ── Node Configuration ──────────────────────────────────── */}
        {selectedNode && (() => {
          const { nodeType, label } = selectedNode.data
          const nodeConf = NODE_TYPE[nodeType] || { color: '#6366f1', Icon: AccountTreeIcon }
          const { color: nodeColor } = nodeConf

          return (
            <ConfigCard accentColor={nodeColor}>
              {/* Node header */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.75 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
                  bgcolor: nodeColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <nodeConf.Icon sx={{ fontSize: 15, color: '#fff' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '13px', fontWeight: 700, color: textPrimary, lineHeight: 1.2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </Typography>
                </Box>
                <NodeTypeBadge type={nodeType} />
              </Stack>

              <Box sx={fieldSx}>
                {/* Node Name */}
                <TextField
                  label="Node Name"
                  value={selectedNode.data.label}
                  onChange={e => updateNodeData(selectedNode.id, { label: e.target.value })}
                  onFocus={e => e.target.select()}
                  fullWidth size="small" sx={{ mb: 1.5 }}
                />

                {/* Location (map-placed nodes) */}
                {selectedNode.data.lat != null && selectedNode.data.lng != null && (
                  <>
                    <SectionLabel color="#94a3b8" mt={0.5}>Location</SectionLabel>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                      <NumericField label="Latitude"
                        value={selectedNode.data.lat ?? ''} fallback={selectedNode.data.lat ?? 0} float fullWidth size="small"
                        InputLabelProps={{ shrink: true }} inputProps={{ step: 0.0001 }}
                        onChange={v => updateNodeData(selectedNode.id, { lat: v })}
                      />
                      <NumericField label="Longitude"
                        value={selectedNode.data.lng ?? ''} fallback={selectedNode.data.lng ?? 0} float fullWidth size="small"
                        InputLabelProps={{ shrink: true }} inputProps={{ step: 0.0001 }}
                        onChange={v => updateNodeData(selectedNode.id, { lng: v })}
                      />
                    </Stack>
                  </>
                )}

                {/* ── DISTRIBUTOR ─────────────────────────────────── */}
                {nodeType === 'distributor' && (
                  <>
                    <SectionLabel color="#3b82f6" mt={0.5}>Inventory</SectionLabel>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                      <NumericField label="Capacity (units)"
                        value={selectedNode.data.capacity ?? 1000} fallback={1000} fullWidth size="small"
                        helperText="Max storage" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { capacity: v })}
                      />
                      <NumericField label="Initial Level (units)"
                        value={selectedNode.data.initial_level ?? 1000} fallback={1000} fullWidth size="small"
                        helperText="Starting stock" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { initial_level: v })}
                      />
                    </Stack>
                    <NumericField label={`Holding Cost (${sym}/unit/day)`}
                      value={selectedNode.data.holding_cost ?? 0.22} fallback={0.22} float fullWidth size="small"
                      inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }}
                      onChange={v => updateNodeData(selectedNode.id, { holding_cost: v })}
                    />

                    <SectionLabel color="#3b82f6">Replenishment Policy</SectionLabel>
                    <PolicyParams nodeData={selectedNode.data} nodeId={selectedNode.id} updateNodeData={updateNodeData}
                      defaults={{ s: 400, S: 1000, R: 7, Q: 500, ss: 100 }} />

                    <SectionLabel color="#3b82f6">Pricing</SectionLabel>
                    <Stack direction="row" spacing={1.5}>
                      <NumericField label={`Buy Price (${sym})`}
                        value={selectedNode.data.buy_price ?? 150} fallback={150} float fullWidth size="small"
                        inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }}
                        onChange={v => updateNodeData(selectedNode.id, { buy_price: v })}
                      />
                      <NumericField label={`Sell Price (${sym})`}
                        value={selectedNode.data.sell_price ?? 300} fallback={300} float fullWidth size="small"
                        inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }}
                        onChange={v => updateNodeData(selectedNode.id, { sell_price: v })}
                      />
                    </Stack>
                  </>
                )}

                {/* ── FACTORY ─────────────────────────────────────── */}
                {nodeType === 'factory' && (
                  <>
                    <SectionLabel color="#f59e0b" mt={0.5}>Inventory</SectionLabel>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                      <NumericField label="Capacity (units)"
                        value={selectedNode.data.capacity ?? 2500} fallback={2500} fullWidth size="small"
                        helperText="Max storage" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { capacity: v })}
                      />
                      <NumericField label="Initial Level (units)"
                        value={selectedNode.data.initial_level ?? 2500} fallback={2500} fullWidth size="small"
                        helperText="Starting stock" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { initial_level: v })}
                      />
                    </Stack>
                    <NumericField label={`Holding Cost (${sym}/unit/day)`}
                      value={selectedNode.data.holding_cost ?? 0.02} fallback={0.02} float fullWidth size="small"
                      inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }}
                      onChange={v => updateNodeData(selectedNode.id, { holding_cost: v })}
                    />

                    <SectionLabel color="#f59e0b">Production</SectionLabel>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                      <NumericField label={`Mfg Cost (${sym})`}
                        value={selectedNode.data.manufacturing_cost ?? 20} fallback={20} float fullWidth size="small"
                        InputLabelProps={{ shrink: true }} inputProps={{ step: '0.01', min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { manufacturing_cost: v })}
                      />
                      <NumericField label="Mfg Time (days)"
                        value={selectedNode.data.manufacturing_time ?? 1} fallback={1} float fullWidth size="small"
                        InputLabelProps={{ shrink: true }} inputProps={{ step: '0.1', min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { manufacturing_time: v })}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                      <NumericField label="Batch Size (units)"
                        value={selectedNode.data.batch_size ?? 1000} fallback={1000} fullWidth size="small"
                        helperText="Units per batch" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { batch_size: v })}
                      />
                      <NumericField label={`Sell Price (${sym})`}
                        value={selectedNode.data.sell_price ?? 30} fallback={30} float fullWidth size="small"
                        InputLabelProps={{ shrink: true }} inputProps={{ step: '0.01', min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { sell_price: v })}
                      />
                    </Stack>

                    <SectionLabel color="#f59e0b">Replenishment Policy</SectionLabel>
                    <PolicyParams nodeData={selectedNode.data} nodeId={selectedNode.id} updateNodeData={updateNodeData}
                      defaults={{ s: 1000, S: 2500, R: 7, Q: 1500, ss: 200 }} />
                  </>
                )}

                {/* ── RETAILER ────────────────────────────────────── */}
                {nodeType === 'retailer' && (
                  <>
                    <SectionLabel color="#8b5cf6" mt={0.5}>Inventory</SectionLabel>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                      <NumericField label="Shelf Capacity (units)"
                        value={selectedNode.data.capacity ?? 500} fallback={500} fullWidth size="small"
                        helperText="Max shelf space" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { capacity: v })}
                      />
                      <NumericField label="Initial Stock (units)"
                        value={selectedNode.data.initial_level ?? 500} fallback={500} fullWidth size="small"
                        helperText="Starting stock" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                        onChange={v => updateNodeData(selectedNode.id, { initial_level: v })}
                      />
                    </Stack>
                    <NumericField label={`Holding Cost (${sym}/unit/day)`}
                      value={selectedNode.data.holding_cost ?? 0.25} fallback={0.25} float fullWidth size="small"
                      inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }}
                      onChange={v => updateNodeData(selectedNode.id, { holding_cost: v })}
                    />

                    <SectionLabel color="#8b5cf6">Replenishment Policy</SectionLabel>
                    <PolicyParams nodeData={selectedNode.data} nodeId={selectedNode.id} updateNodeData={updateNodeData}
                      defaults={{ s: 200, S: 500, R: 5, Q: 300, ss: 50 }} />

                    <SectionLabel color="#8b5cf6">Pricing</SectionLabel>
                    <Stack direction="row" spacing={1.5}>
                      <NumericField label={`Buy Price (${sym})`}
                        value={selectedNode.data.buy_price ?? 300} fallback={300} float fullWidth size="small"
                        inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }}
                        onChange={v => updateNodeData(selectedNode.id, { buy_price: v })}
                      />
                      <NumericField label={`Sell Price (${sym})`}
                        value={selectedNode.data.sell_price ?? 400} fallback={400} float fullWidth size="small"
                        inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }}
                        onChange={v => updateNodeData(selectedNode.id, { sell_price: v })}
                      />
                    </Stack>
                  </>
                )}

                {/* ── SUPPLIER ────────────────────────────────────── */}
                {nodeType === 'supplier' && (
                  <>
                    <SectionLabel color="#10b981" mt={0.5}>Supplier Type</SectionLabel>
                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                      <InputLabel>Supplier Type</InputLabel>
                      <Select
                        value={selectedNode.data.supplier_type ?? 'infinite'}
                        label="Supplier Type"
                        onChange={e => updateNodeData(selectedNode.id, { supplier_type: e.target.value })}
                      >
                        <MenuItem value="infinite">Infinite Capacity</MenuItem>
                        <MenuItem value="finite">Finite Capacity</MenuItem>
                      </Select>
                    </FormControl>

                    {selectedNode.data.supplier_type === 'finite' && (
                      <>
                        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                          <NumericField label="Capacity (units)"
                            value={selectedNode.data.capacity ?? 10000} fallback={10000} fullWidth size="small"
                            helperText="Max storage" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                            onChange={v => updateNodeData(selectedNode.id, { capacity: v })}
                          />
                          <NumericField label="Initial Level (units)"
                            value={selectedNode.data.initial_level ?? 10000} fallback={10000} fullWidth size="small"
                            helperText="Starting stock" InputLabelProps={{ shrink: true }} inputProps={{ min: 0 }}
                            onChange={v => updateNodeData(selectedNode.id, { initial_level: v })}
                          />
                        </Stack>
                        <NumericField label={`Holding Cost (${sym}/unit/day)`}
                          value={selectedNode.data.holding_cost ?? 0.01} fallback={0.01} float fullWidth size="small"
                          inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }}
                          onChange={v => updateNodeData(selectedNode.id, { holding_cost: v })}
                        />
                      </>
                    )}
                  </>
                )}
              </Box>
            </ConfigCard>
          )
        })()}

        {/* ── Demands Configuration ───────────────────────────────── */}
        {demands.length > 0 && (
          <>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 2, height: 12, borderRadius: 1, bgcolor: '#f59e0b' }} />
              <LocalShippingIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
              <Typography sx={{ fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#f59e0b' }}>
                Customer Demands
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label={demands.length} size="small"
                sx={{ height: 17, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(245,158,11,0.12)', color: '#f59e0b', '& .MuiChip-label': { px: 0.75 } }} />
            </Box>

            {demands.map(demand => {
              const hasAdvancedPattern      = demand.pattern && demand.pattern !== 'constant'
              const hasAdvancedDistribution = demand.distribution_type && demand.distribution_type !== 'fixed'
              const targetNode = nodes.find(n => n.id === demand.target_node)

              return (
                <ConfigCard key={demand.id} accentColor="#f59e0b">
                  {/* Demand header */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <TextField
                      value={demand.name || 'Unnamed Demand'}
                      onChange={e => updateDemand(demand.id, { name: e.target.value })}
                      variant="standard"
                      sx={{
                        flex: 1,
                        '& .MuiInput-input': { fontWeight: 700, fontSize: '13px', color: textPrimary },
                        '& .MuiInput-underline:before': { borderBottomColor: borderMid },
                        '& .MuiInput-underline:hover:before': { borderBottomColor: '#f59e0b' },
                        '& .MuiInput-underline:after': { borderBottomColor: '#f59e0b' },
                      }}
                      placeholder="Demand Name"
                    />
                    <IconButton size="small" onClick={() => deleteDemand(demand.id)}
                      sx={{ width: 26, height: 26, color: textSecondary, '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Stack>

                  <Box sx={fieldSx}>
                    {/* Target node */}
                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                      <InputLabel>Target Node</InputLabel>
                      <Select
                        value={targetNode ? demand.target_node : (nodes.find(n => n.data.nodeType === 'distributor' || n.data.nodeType === 'retailer')?.id || nodes[0]?.id || '')}
                        label="Target Node"
                        onChange={e => updateDemand(demand.id, { target_node: e.target.value })}
                      >
                        {nodes.length === 0
                          ? <MenuItem value="" disabled><em>No nodes available</em></MenuItem>
                          : nodes.map(n => <MenuItem key={n.id} value={n.id}>{n.data.label} ({n.data.nodeType})</MenuItem>)
                        }
                      </Select>
                    </FormControl>

                    {!targetNode && (
                      <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, borderRadius: '8px', fontSize: '11px' }}>
                        Target node deleted — select a new target
                      </Alert>
                    )}

                    {/* Advanced pattern badge */}
                    {hasAdvancedPattern && (
                      <Box sx={{
                        mb: 1.5, p: 1.25, borderRadius: '8px',
                        bgcolor: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.05)',
                        border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.15)'}`,
                      }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                          <ShowChartIcon sx={{ fontSize: 13, color: accent }} />
                          <Typography sx={{ fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: accent }}>
                            Arrival Pattern
                          </Typography>
                          <Chip label={demand.pattern.toUpperCase()} size="small"
                            sx={{ height: 16, fontSize: '9.5px', fontWeight: 700, ml: 0.5, bgcolor: `${accent}18`, color: accent, '& .MuiChip-label': { px: 0.625 } }} />
                        </Stack>
                        <Typography sx={{ fontSize: '10.5px', color: textSecondary, lineHeight: 1.6 }}>
                          {demand.pattern === 'random' && demand.patternParams && (
                            <><strong>Avg Interval:</strong> {demand.patternParams.avgInterval || 1} days · <strong>Variance:</strong> {demand.patternParams.variance || 0.2}</>
                          )}
                          {demand.pattern === 'seasonal' && demand.patternParams && (
                            <><strong>Base:</strong> {demand.patternParams.baseInterval || 1}d · <strong>Amplitude:</strong> {demand.patternParams.amplitude || 0.5} · <strong>Period:</strong> {demand.patternParams.period || 10}d</>
                          )}
                          {demand.pattern === 'trending' && demand.patternParams && (
                            <><strong>Start:</strong> {demand.patternParams.startInterval || 1}d → <strong>End:</strong> {demand.patternParams.endInterval || 2}d over {demand.patternParams.duration || 30}d</>
                          )}
                          {demand.pattern === 'burst' && demand.patternParams && (
                            <><strong>Normal:</strong> {demand.patternParams.normalInterval || 1}d · <strong>Burst:</strong> {demand.patternParams.burstInterval || 0.2}d every {demand.patternParams.burstFrequency || 5} orders</>
                          )}
                        </Typography>
                      </Box>
                    )}

                    {/* Advanced distribution badge */}
                    {hasAdvancedDistribution && (
                      <Box sx={{
                        mb: 1.5, p: 1.25, borderRadius: '8px',
                        bgcolor: isDark ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.05)',
                        border: `1px solid ${isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.15)'}`,
                      }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                          <TrendingUpIcon sx={{ fontSize: 13, color: '#8b5cf6' }} />
                          <Typography sx={{ fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8b5cf6' }}>
                            Quantity Distribution
                          </Typography>
                          <Chip label={demand.distribution_type.toUpperCase()} size="small"
                            sx={{ height: 16, fontSize: '9.5px', fontWeight: 700, ml: 0.5, bgcolor: 'rgba(139,92,246,0.15)', color: '#8b5cf6', '& .MuiChip-label': { px: 0.625 } }} />
                        </Stack>
                        <Typography sx={{ fontSize: '10.5px', color: textSecondary, lineHeight: 1.6 }}>
                          {demand.distribution_type === 'normal' && demand.distribution_params && (
                            <><strong>μ:</strong> {demand.distribution_params.mean || demand.order_quantity} units · <strong>σ:</strong> {demand.distribution_params.std_dev || 50} units</>
                          )}
                          {demand.distribution_type === 'uniform' && demand.distribution_params && (
                            <><strong>Min:</strong> {demand.distribution_params.min || demand.order_quantity - 100} → <strong>Max:</strong> {demand.distribution_params.max || demand.order_quantity + 100} units</>
                          )}
                          {demand.distribution_type === 'poisson' && demand.distribution_params && (
                            <><strong>λ:</strong> {demand.distribution_params.lambda || demand.order_quantity} events/period</>
                          )}
                          {demand.distribution_type === 'exponential' && demand.distribution_params && (
                            <><strong>β:</strong> {demand.distribution_params.scale || demand.order_quantity} time units</>
                          )}
                        </Typography>
                      </Box>
                    )}

                    {/* Basic demand fields */}
                    <Stack spacing={1.25}>
                      {!hasAdvancedPattern && (
                        <NumericField label="Arrival Interval (days)"
                          value={demand.arrival_interval ?? 1} fallback={1} float fullWidth size="small"
                          inputProps={{ step: '0.1', min: 0.1 }} helperText="Time between orders"
                          InputLabelProps={{ shrink: true }}
                          onChange={v => updateDemand(demand.id, { arrival_interval: v })}
                        />
                      )}
                      {!hasAdvancedDistribution && (
                        <NumericField label="Order Quantity (units)"
                          value={demand.order_quantity ?? 400} fallback={400} fullWidth size="small"
                          inputProps={{ min: 1 }} helperText="Units per order"
                          InputLabelProps={{ shrink: true }}
                          onChange={v => updateDemand(demand.id, { order_quantity: v })}
                        />
                      )}
                      <Stack direction="row" spacing={1.5}>
                        <NumericField label={`Delivery Cost (${sym})`}
                          value={demand.delivery_cost ?? 10} fallback={10} float fullWidth size="small"
                          inputProps={{ step: '0.01', min: 0 }} InputLabelProps={{ shrink: true }}
                          onChange={v => updateDemand(demand.id, { delivery_cost: v })}
                        />
                        <NumericField label="Lead Time (days)"
                          value={demand.lead_time ?? 5} fallback={5} float fullWidth size="small"
                          inputProps={{ step: '0.1', min: 0 }} InputLabelProps={{ shrink: true }}
                          onChange={v => updateDemand(demand.id, { lead_time: v })}
                        />
                      </Stack>
                    </Stack>

                    {/* Advanced pattern button */}
                    <Button
                      size="small"
                      variant={hasAdvancedPattern || hasAdvancedDistribution ? 'contained' : 'outlined'}
                      onClick={() => handleAdvancedEdit(demand)}
                      fullWidth
                      sx={{
                        mt: 1.75, textTransform: 'none', fontSize: '11.5px', fontWeight: 600,
                        borderRadius: '8px', py: 0.75,
                        ...(hasAdvancedPattern || hasAdvancedDistribution
                          ? { bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, boxShadow: 'none' }
                          : { borderColor: border, color: textSecondary, '&:hover': { borderColor: '#f59e0b', color: '#f59e0b', bgcolor: 'rgba(245,158,11,0.06)' } }
                        ),
                      }}
                      startIcon={<SettingsIcon sx={{ fontSize: '14px !important' }} />}
                    >
                      {hasAdvancedPattern || hasAdvancedDistribution
                        ? 'Edit Advanced Pattern'
                        : 'Configure Advanced Pattern'
                      }
                    </Button>
                  </Box>
                </ConfigCard>
              )
            })}
          </>
        )}
      </Box>

      <AdvancedDemandDialog
        open={showAdvancedDemand}
        onClose={handleCloseAdvanced}
        onSave={handleSaveAdvanced}
        demand={editingDemand}
        nodes={nodes}
      />
    </Box>
  )
}

export default ConfigPanel
